import React, { useEffect, useState, useMemo } from 'react'
import { Box, Card, Button, CardContent, Grid, TextField, Typography } from '@mui/material'
import ReCAPTCHA from "react-google-recaptcha";
import SendIcon from '@mui/icons-material/Send';
import styled from '@emotion/styled';
import {Alert, variants} from '../alert/Alert';
import Spinner from '../spinner/Spinner';
import { API } from '../../interceptors/axios';
import ThemeAmbientOverlay from '../theme-ambient-overlay/ThemeAmbientOverlay';
import { useTheme } from '../../theme/theme-provider';
import { fetchAuth } from '../../utils/auth';


const Contact = ({ sendPage }) => {
    const { themeMode } = useTheme();
    const isDark = themeMode === 'dark';

    const [captchaValidated, setCaptchaValidated] = useState(false)
    const [messageSent, setMessageSent] = useState(false)
    const [sendFailed, setSendFailed] = useState(false)
    const [rateLimited, setRateLimited] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        sendPage("contact")
        window.scrollTo(0,0)
    }, [])

    const onChange = (value) => {
        setCaptchaValidated(Boolean(value))
      }

    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [subject, setSubject] = useState('')
    const [message, setMessage] = useState('')
   
    const onSubmit = async (e) =>  {
        e.preventDefault()
        if (isLoading || !captchaValidated) return;

        setIsLoading(true)
        setMessageSent(false)
        setSendFailed(false)
        setRateLimited(false)

        try {
            await fetchAuth();
            await API.post(`/contacts/`, { name, email, subject, message });

            setName('')
            setEmail('')
            setSubject('')
            setMessage('')
            setMessageSent(true)
        } catch (err) {
            console.error('Failed to send contact email:', err)
            if (err.response?.status === 429) {
                setRateLimited(true)
            } else {
                setSendFailed(true)
            }
        } finally {
            setIsLoading(false)
        }
    }

    const StyledSubmitButton = useMemo(() => styled(Button)({
            minWidth: 190,
            top: 10,
            marginTop: 10,
            borderRadius: 8,
            padding: '10px 18px',
            letterSpacing: '0.08em',
            fontFamily: '"Courier New", monospace',
            fontWeight: 'bold',
            backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : '#2f4c4c',
            color: isDark ? 'rgba(242, 211, 153, 0.95)' : '#fffaf4',
            border: isDark ? '1px solid rgba(242, 211, 153, 0.55)' : '1px solid #2f4c4c',
            boxShadow: isDark ? '0 8px 20px rgba(0,0,0,0.35)' : '0 8px 20px rgba(0,0,0,0.18)',
            transition: 'all 0.2s ease',
            '&:hover': {
                color: isDark ? 'black' : '#fffaf4',
                backgroundColor: isDark ? 'rgba(242, 211, 153, 0.9)' : '#3d6262',
                transform: 'translateY(-1px)',
            },
            '&.Mui-disabled': {
                opacity: 1,
                color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.75)',
                backgroundColor: isDark ? 'rgba(32,32,32,0.9)' : 'rgba(235,235,235,0.95)',
                borderColor: isDark ? 'rgba(242,211,153,0.3)' : 'rgba(0,0,0,0.35)',
                boxShadow: isDark ? '0 6px 16px rgba(0,0,0,0.3)' : '0 6px 16px rgba(0,0,0,0.12)'
            }
        }), [isDark]);

    const textFieldStyles = useMemo(() => ({
        width: '100%',
        boxSizing: 'border-box',
        '& .MuiInputBase-root': {
            backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.85)',
            borderRadius: '6px',
            fontFamily: '"Courier New", monospace',
            width: '100%',
            boxSizing: 'border-box'
        },
        '& .MuiInputLabel-root': {
            color: isDark ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.6)',
            fontFamily: '"Courier New", monospace',
            backgroundColor: 'transparent',
            paddingRight: '4px'
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: isDark ? 'rgba(242, 211, 153, 0.95)' : '#2f4c4c'
        },
        '& .MuiInputLabel-shrink': {
            backgroundColor: isDark ? 'rgba(30,30,30,0.95)' : 'rgba(255,250,244,0.95)',
            paddingLeft: '4px',
            paddingRight: '4px'
        },
        '& .MuiOutlinedInput-input': {
            color: isDark ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.9)',
            WebkitTextFillColor: isDark ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.9)'
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: isDark ? 'rgba(242, 211, 153, 0.35)' : 'rgba(0,0,0,0.25)'
        },
        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: isDark ? 'rgba(242, 211, 153, 0.55)' : 'rgba(0,0,0,0.4)'
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: isDark ? 'rgba(242, 211, 153, 0.8)' : '#2f4c4c',
            borderWidth: '2px'
        },
        '& input:-webkit-autofill': {
            WebkitBoxShadow: isDark ? '0 0 0 100px rgba(255,255,255,0.08) inset' : '0 0 0 100px rgba(255,255,255,0.85) inset',
            WebkitTextFillColor: isDark ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.9)',
            borderRadius: '6px'
        },
        '& textarea:-webkit-autofill': {
            WebkitBoxShadow: isDark ? '0 0 0 100px rgba(255,255,255,0.08) inset' : '0 0 0 100px rgba(255,255,255,0.85) inset',
            WebkitTextFillColor: isDark ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.9)',
            borderRadius: '6px'
        }
    }), [isDark]);

    return (
        <Box align="center" sx={{
            backgroundColor: 'var(--background-color)',            
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            width: "100%",
            minHeight: '100vh',
            justifyContent: "center",
            position: 'relative',
            overflow: 'hidden',
        }}>
            <ThemeAmbientOverlay speed={30} />
            <Box sx={{ position: 'relative', zIndex: 2, width: '100%', pt: { xs: 2, md: 2 }, pb: { xs: 4, md: 6 } }}>
            <Typography
                align="center"
                color={isDark ? 'white' : 'rgba(22,22,22,0.94)'}
                variant="h2"
                sx={{ 
                    fontWeight: 900,
                    mb: 4,
                    textShadow: isDark ? '4px 4px black' : '2px 2px rgba(255,255,255,0.65)', 
                    fontFamily: 'Light-1',
                    fontSize: { 
                            xs: '2.4rem',
                            sm: '3rem',
                            md: '3.5rem',
                            lg: '4rem'
                        },
                     }}>
                Get in Touch
            </Typography>
            <Box sx={{ width: '80%', maxWidth: 900, mx: 'auto', mb: 3 }}>
                <Card sx={{
                    position: 'relative',
                    border: isDark ? '1px solid rgba(242, 211, 153, 0.25)' : '1px solid rgba(0,0,0,0.1)',
                    boxShadow: isDark ? '0 12px 30px rgba(0,0,0,0.25)' : '0 10px 24px rgba(0,0,0,0.12)',
                    textAlign: 'left',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: isDark
                            ? 'linear-gradient(90deg, rgba(0,0,0,0.05) 0%, rgba(52,52,52,0.8) 15%, rgba(52,52,52,0.8) 85%, rgba(0,0,0,0.05) 100%)'
                            : 'linear-gradient(90deg, rgba(255,250,244,0.1) 0%, rgba(255,250,244,0.92) 15%, rgba(255,250,244,0.92) 85%, rgba(255,250,244,0.1) 100%)',
                        backdropFilter: 'blur(8px)',
                        borderRadius: 'inherit',
                        zIndex: -1
                    }
                }}>
                    <CardContent>
                        <Typography
                            color={isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.8)'}
                            sx={{
                                fontFamily: '"Courier New", monospace',
                                fontWeight: 600,
                                lineHeight: 1.7,
                                textAlign: 'center',
                                mb: 3
                            }}
                        >
                            Whether you&apos;re looking for original music, sound design, or simply want to
                            discuss a project, feel free to reach out.
                        </Typography>
                        <Box sx={{ display: 'grid', gap: 1.5, justifyItems: 'center' }}>
                            <Typography
                                color={isDark ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.9)'}
                                sx={{ fontFamily: '"Courier New", monospace', fontWeight: 'bold' }}
                            >
                                Vadym D.
                            </Typography>
                            <Typography
                                color={isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.85)'}
                                sx={{ fontFamily: '"Courier New", monospace' }}
                            >
                                <strong>Email:</strong>{' '}
                                <span style={{ color: isDark ? '#F2D399' : '#2f4c4c', fontWeight: 700 }}>distorise@gmail.com</span>
                            </Typography>
                            <Typography
                                color={isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.85)'}
                                sx={{ fontFamily: '"Courier New", monospace' }}
                            >
                                <strong>Instagram:</strong>{' '}
                                <span style={{ color: isDark ? '#F2D399' : '#2f4c4c', fontWeight: 700 }}>@alan_distorise</span>
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
            <Card sx={{
                width: '80%',
                maxWidth: 900,
                position: 'relative',
                border: isDark ? '1px solid rgba(242, 211, 153, 0.25)' : '1px solid rgba(0,0,0,0.1)',
                boxShadow: isDark ? '0 12px 30px rgba(0,0,0,0.35)' : '0 10px 24px rgba(0,0,0,0.18)',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: isDark
                        ? 'linear-gradient(90deg, rgba(0,0,0,0.05) 0%, rgba(52,52,52,0.85) 15%, rgba(52,52,52,0.85) 85%, rgba(0,0,0,0.05) 100%)'
                        : 'linear-gradient(90deg, rgba(255,250,244,0.1) 0%, rgba(255,250,244,0.92) 15%, rgba(255,250,244,0.92) 85%, rgba(255,250,244,0.1) 100%)',
                    backdropFilter: 'blur(8px)',
                    borderRadius: 'inherit',
                    zIndex: -1
                }
            }}> 
                <CardContent>   
                    <form onSubmit={e => onSubmit(e)}>                    
                    <Grid
                        container
                        direction="column"
                        justifyContent="center"
                        alignItems="stretch"
                        sx={{ width: '100%' }}>
                        
                        <Grid item sx={{ padding: 1, width: '100%' }}>
                            <TextField fullWidth required 
                                onChange={(e) => {setName(e.target.value)}}
                                value={name} type="text"
                                label="Name" placeholder='Type your name' variant='outlined'
                                sx={textFieldStyles} />
                        </Grid>
                        <Grid item sx={{ padding: 1, width: '100%' }}>
                            <TextField fullWidth required 
                                onChange={(e) => {setEmail(e.target.value)}}
                                value={email} type='email' 
                                label="Email" placeholder='Type your email'
                                sx={textFieldStyles} />
                        </Grid>
                        <Grid item sx={{ padding: 1, width: '100%' }}>
                            <TextField fullWidth required 
                                onChange={(e) => {setSubject(e.target.value)}}
                                value={subject} type='text' 
                                label="Subject" placeholder='Type your subject'
                                sx={textFieldStyles} />
                        </Grid>
                        <Grid item sx={{ padding: 1, width: '100%' }}>
                            <TextField fullWidth required 
                                onChange={(e) => {setMessage(e.target.value)}}
                                value={message} type='text' 
                                multiline rows={4} label="Message" placeholder='Type your message'
                                sx={textFieldStyles} />
                        </Grid>
                    </Grid>
                        {(messageSent && <Alert variant={variants[1]}/>)}
                        {(sendFailed && <Alert variant={variants[0]}/>)}
                        {(rateLimited && <Alert variant={variants[2]}/>)}
                    <Box 
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2,
                            mt: 2,
                            pt: 2,
                            borderTop: isDark ? '1px solid rgba(242, 211, 153, 0.25)' : '1px solid rgba(0,0,0,0.15)'
                        }}
                    >
                        <Box sx={{
                            position: 'relative',
                            borderRadius: '6px',
                            overflow: 'hidden',
                            // Inset overlay covers the white border drawn inside Google's iframe
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                inset: 0,
                                borderRadius: '6px',
                                boxShadow: `inset 0 0 0 3px ${isDark ? '#1a1a1a' : '#f6f1ea'}`,
                                pointerEvents: 'none',
                                zIndex: 2,
                            },
                        }}>
                            <ReCAPTCHA
                                sitekey="6LeW-fYoAAAAAD6aN4LiF5IITLXDAoH1HjT8JtYV"
                                onChange={onChange}
                                theme={isDark ? 'dark' : 'light'}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <StyledSubmitButton
                                type="submit"
                                variant="contained"
                                disabled={!captchaValidated || isLoading}
                                endIcon={!isLoading ? <SendIcon /> : null}>
                                {isLoading ? 'SENDING...' : 'SEND MESSAGE'}
                            </StyledSubmitButton>
                            {(isLoading && <Spinner />)}
                        </Box>
                    </Box>
                    </form>
                </CardContent>
 
            </Card>
            </Box>
        </Box>
    )
}

export default Contact